import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import {
  HotkeysProvider as OriginalHotkeysProvider,
  useHotkeysContext as useOriginalHotkeysContext
} from "react-hotkeys-hook";
import Immutable from "immutable";
import HotkeysContext from "contexts/HotkeysContext";
import { isMacOS } from "util/OSUtils";
import AppConfig from "util/AppConfig";
import { FEATURE_FLAG } from "components/quick-jump/Constants";
const viewActions = {
  undo: { keys: "mod+shift+z", description: "Undo last action" },
  redo: { keys: "mod+shift+y", description: "Redo last action" }
};
const isQuickJumpEnabled = AppConfig.isFeatureEnabled(FEATURE_FLAG);
export const hotKeysCollections = {
  general: {
    title: "General",
    description: "General keyboard shortcuts",
    actions: {
      "show-hotkeys-modal": { keys: "shift+?", displayKeys: "?", description: "Show available keyboard shorts" },
      ...isQuickJumpEnabled ? { "show-quick-jump-modal": { keys: ["mod+k", "mod+space"], description: "Quick Jump" } } : {},
      "submit-form": { keys: "enter", description: "Submit form" },
      "close-modal": { keys: "esc", description: "Close modal" },
      "show-scratchpad-modal": { keys: "mod+/", description: "Show scratchpad" }
    }
  },
  search: {
    title: "Search",
    description: "Keyboard shortcuts for search page",
    actions: {
      ...viewActions,
      save: { keys: "mod+s", description: "Save search" },
      "save-as": { keys: "mod+shift+s", description: "Save search as" }
    }
  },
  dashboard: {
    title: "Dashboard",
    description: "Keyboard shortcuts for dashboard page",
    actions: {
      ...viewActions,
      save: { keys: "mod+s", description: "Save dashboard" },
      "save-as": { keys: "mod+shift+s", description: "Save dashboard as" }
    }
  },
  "query-input": {
    title: "Query Input",
    description: "Keyboard shortcuts for query input in search bar, available when input is focussed.",
    // Please note, any changes to keybindings also need to be made in the query input component.
    actions: {
      "submit-search": { keys: "return", description: "Execute the search" },
      "insert-newline": { keys: "shift+return", description: "Create a new line" },
      "create-search-filter": { keys: "alt+return", description: "Create search filter based on current query" },
      "show-suggestions": {
        keys: isMacOS() ? "alt+space" : "mod+space",
        description: "Show suggestions, displays query history when input is empty"
      },
      "show-history": { keys: "alt+shift+h", description: "View your search query history" }
    }
  },
  scratchpad: {
    title: "Scratchpad",
    description: "Scratchpad shortcuts",
    actions: {
      clear: { keys: ["mod+backspace", "mod+del"], description: "Clear scratchpad" },
      copy: { keys: "shift+mod+c", description: "Copy scratchpad" }
    }
  }
};
const CustomHotkeysProvider = ({ children }) => {
  const [activeHotkeys, setActiveHotkeys] = useState(Immutable.Map());
  const { enabledScopes } = useOriginalHotkeysContext();
  const [showHotkeysModal, setShowHotkeysModal] = useState(false);
  const addActiveHotkey = useCallback(
    ({
      scope,
      actionKey,
      options
    }) => {
      setActiveHotkeys((cur) => cur.set(`${scope}.${actionKey}`, { options }));
    },
    []
  );
  const removeActiveHotkey = useCallback(({ scope, actionKey }) => {
    setActiveHotkeys((cur) => cur.delete(`${scope}.${actionKey}`));
  }, []);
  const value = useMemo(
    () => ({
      enabledScopes,
      hotKeysCollections,
      activeHotkeys,
      addActiveHotkey,
      removeActiveHotkey,
      showHotkeysModal,
      setShowHotkeysModal
    }),
    [activeHotkeys, addActiveHotkey, enabledScopes, removeActiveHotkey, showHotkeysModal]
  );
  return /* @__PURE__ */ React.createElement(HotkeysContext.Provider, { value }, children);
};
const HotkeysProvider = ({ children }) => /* @__PURE__ */ React.createElement(OriginalHotkeysProvider, null, /* @__PURE__ */ React.createElement(CustomHotkeysProvider, null, children));
export default HotkeysProvider;
